import React, { useState, useRef, useEffect } from 'react';
import WrapperComponent from '../commonComponents/WrapperComponent';
import PaginationComponent from '../commonComponents/PaginationComponent';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { chatbotUrl } from '../../util/config';

const STATUS_CONFIG = {
    ready:      { icon: 'bi-check-circle', color: '#16a34a', bg: '#f0fdf4', border: '#bbf7d0', label: 'Ready'      },
    processing: { icon: 'bi-clock',        color: '#d97706', bg: '#fffbeb', border: '#fde68a', label: 'Processing' },
    failed:     { icon: 'bi-x-circle',     color: '#dc2626', bg: '#fef2f2', border: '#fecaca', label: 'Failed'     },
    pending:    { icon: 'bi-clock',        color: '#1565c0', bg: '#eff6ff', border: '#bfdbfe', label: 'Pending'    },
};
const TYPE_COLORS = {
    pdf:  { color: '#dc2626', bg: '#fef2f2' },
    docx: { color: '#1565c0', bg: '#eff6ff' },
    xlsx: { color: '#16a34a', bg: '#f0fdf4' },
    txt:  { color: '#6b7280', bg: '#f9fafb' },
    csv:  { color: '#16a34a', bg: '#f0fdf4' },
};
const formatSize = (kb) => kb >= 1024 ? `${(kb / 1024).toFixed(1)} MB` : `${kb} KB`;
const LIMIT = 10;

// ── Confirm Modal ─────────────────────────────────────────────────────────────
const ConfirmModal = ({ open, onClose, onConfirm, loading, message }) => {
    if (!open) return null;
    return (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.45)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1050 }}>
            <div style={{ background:'white', borderRadius:12, width:'100%', maxWidth:420, boxShadow:'0 20px 40px rgba(0,0,0,0.2)', overflow:'hidden' }}>
                <div style={{ display:'flex', alignItems:'center', gap:12, padding:'16px 20px', borderBottom:'1px solid #f3f4f6', background:'#fff7f7' }}>
                    <div style={{ width:38, height:38, borderRadius:10, background:'#fee2e2', display:'flex', alignItems:'center', justifyContent:'center' }}>
                        <i className="bi bi-exclamation-triangle" style={{ fontSize:16, color:'#dc2626' }}></i>
                    </div>
                    <h6 style={{ margin:0, fontSize:15, fontWeight:700, color:'#111827' }}>Delete Document</h6>
                    <button onClick={onClose} style={{ background:'none', border:'none', cursor:'pointer', color:'#6b7280', fontSize:13, marginLeft:'auto' }}><i className="bi bi-x-lg"></i></button>
                </div>
                <div style={{ padding:20 }}><p style={{ fontSize:14, color:'#6b7280', margin:0, lineHeight:1.6 }}>{message}</p></div>
                <div style={{ display:'flex', justifyContent:'flex-end', gap:10, padding:'14px 20px', borderTop:'1px solid #f3f4f6' }}>
                    <button onClick={onClose} disabled={loading} style={{ padding:'8px 16px', background:'#f3f4f6', color:'#374151', border:'1px solid #e5e7eb', borderRadius:8, fontSize:13, fontWeight:600, cursor:'pointer' }}>Cancel</button>
                    <button onClick={onConfirm} disabled={loading} style={{ display:'inline-flex', alignItems:'center', gap:6, padding:'8px 16px', background:'#dc2626', color:'white', border:'none', borderRadius:8, fontSize:13, fontWeight:600, cursor:'pointer' }}>
                        {loading ? <><span style={{ width:14, height:14, border:'2px solid rgba(255,255,255,0.3)', borderTopColor:'white', borderRadius:'50%', display:'inline-block', animation:'spin 0.7s linear infinite' }}></span> Deleting…</> : 'Delete'}
                    </button>
                </div>
            </div>
        </div>
    );
};

// ── Upload Zone ───────────────────────────────────────────────────────────────
const UploadZone = ({ tags, onUploaded }) => {
    const [dragging,  setDragging]  = useState(false);
    const [tagId,     setTagId]     = useState('');
    const [uploading, setUploading] = useState(false);
    const [file,      setFile]      = useState(null);
    const inputRef = useRef();

    const handleDrop = (e) => { e.preventDefault(); setDragging(false); const f = e.dataTransfer.files[0]; if (f) setFile(f); };

    const handleUpload = async () => {
        if (!file)  { toast.error('Select a file first'); return; }
        if (!tagId) { toast.error('Select a tag for this document'); return; }
        setUploading(true);
        const token = localStorage.getItem('token');
        try {
            const formData = new FormData();
            formData.append('file',   file);
            formData.append('tag_id', tagId);
            const res = await fetch(`${chatbotUrl}/documents/upload`, {
                method: 'POST',
                headers: { Authorization: `Bearer ${token}` },
                body: formData,
            });
            if (!res.ok) { const err = await res.json().catch(() => ({})); throw new Error(err?.detail || 'Upload failed'); }
            toast.success(`"${file.name}" uploaded — processing started`, { autoClose: 3000 });
            onUploaded();
            setFile(null); setTagId('');
        } catch (err) {
            toast.error(err.message || 'Upload failed', { autoClose: 3000 });
        } finally { setUploading(false); }
    };

    return (
        <div style={s.uploadCard}>
            <h6 style={s.uploadTitle}>Upload Document</h6>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 220px auto', gap:12, alignItems:'end' }}>
                <div onDragOver={e => { e.preventDefault(); setDragging(true); }} onDragLeave={() => setDragging(false)}
                    onDrop={handleDrop} onClick={() => inputRef.current.click()}
                    style={{ border:`2px dashed ${dragging ? '#1565c0' : file ? '#16a34a' : '#e5e7eb'}`, borderRadius:12, padding:'20px 24px', textAlign:'center', cursor:'pointer', background:dragging ? '#eff6ff' : file ? '#f0fdf4' : '#f9fafb', transition:'all 0.2s' }}>
                    <input ref={inputRef} type="file" style={{ display:'none' }} accept=".pdf,.docx,.xlsx,.csv,.txt" onChange={e => setFile(e.target.files[0])}/>
                    {file ? (
                        <div style={{ display:'flex', alignItems:'center', justifyContent:'center', gap:8 }}>
                            <i className="bi bi-check-circle-fill" style={{ color:'#16a34a', fontSize:16 }}></i>
                            <span style={{ fontSize:13, color:'#16a34a', fontWeight:600 }}>{file.name}</span>
                            <span style={{ fontSize:11, color:'#9ca3af' }}>({formatSize(Math.round(file.size/1024))})</span>
                        </div>
                    ) : (
                        <div>
                            <i className="bi bi-cloud-upload" style={{ fontSize:24, color:'#9ca3af', display:'block', margin:'0 auto 8px' }}></i>
                            <p style={{ fontSize:13, color:'#6b7280', fontWeight:500, margin:0 }}>Drop file or click to browse</p>
                            <p style={{ fontSize:11, color:'#9ca3af', marginTop:4 }}>PDF, DOCX, XLSX, CSV, TXT — max 50MB</p>
                        </div>
                    )}
                </div>
                <div>
                    <label style={s.fieldLabel}>Assign to Tag</label>
                    <select style={s.select} value={tagId} onChange={e => setTagId(e.target.value)}>
                        <option value="">Select tag...</option>
                        {tags.map(t => <option key={t.id||t._id} value={t.id||t._id}>{t.name||t.tag_name}</option>)}
                    </select>
                </div>
                <button onClick={handleUpload} disabled={uploading||!file||!tagId} style={{ padding:'10px 20px', background:uploading||!file||!tagId ? '#e5e7eb':'linear-gradient(to right, #2388e0, #1565c0)', color:uploading||!file||!tagId?'#9ca3af':'#fff', border:'none', borderRadius:10, fontSize:13, fontWeight:600, cursor:uploading||!file||!tagId?'not-allowed':'pointer', display:'flex', alignItems:'center', gap:6, whiteSpace:'nowrap', height:42 }}>
                    {uploading ? <><span style={{ width:14, height:14, border:'2px solid rgba(255,255,255,0.3)', borderTopColor:'white', borderRadius:'50%', display:'inline-block', animation:'spin 0.7s linear infinite' }}></span>Uploading…</> : <><i className="bi bi-upload"></i>Upload</>}
                </button>
            </div>
        </div>
    );
};

// ── Main Component ───────────────────────────────────────────────────────────
const KnowledgeBase = () => {
    const [docs,          setDocs]          = useState([]);
    const [tags,          setTags]          = useState([]);
    const [search,        setSearch]        = useState('');
    const [tagFilter,     setTagFilter]     = useState('');
    const [statusFilter,  setStatusFilter]  = useState('');
    const [currentPage,   setCurrentPage]   = useState(1);
    const [totalCount,    setTotalCount]    = useState(0);
    const [loading,       setLoading]       = useState(true);
    const [deleteDoc,     setDeleteDoc]     = useState(null);
    const [deleteLoading, setDeleteLoading] = useState(false);

    const fetchTags = async () => {
        const token = localStorage.getItem('token');
        try {
            const res = await fetch(`${chatbotUrl}/tags?limit=100`, { headers: { Authorization: `Bearer ${token}` } });
            const data = await res.json();
            setTags(data?.data?.items || data?.data || []);
        } catch (e) { /* ignored */ }
    };

    const fetchDocs = async (page = 1) => {
        setLoading(true);
        const token = localStorage.getItem('token');
        try {
            const params = new URLSearchParams({ limit: LIMIT, skip: (page-1)*LIMIT, is_latest: true });
            if (search)       params.append('search',  search);
            if (tagFilter)    params.append('tag_id',  tagFilter);
            if (statusFilter) params.append('status',  statusFilter);
            const res = await fetch(`${chatbotUrl}/documents?${params}`, { headers: { Authorization: `Bearer ${token}` } });
            if (!res.ok) throw new Error();
            const data = await res.json();
            setDocs(data?.data?.items || data?.data || []);
            setTotalCount(data?.data?.total || 0);
        } catch (e) {
            toast.error('Failed to load documents', { autoClose: 3000 });
        } finally { setLoading(false); }
    };

    useEffect(() => { fetchTags(); fetchDocs(1); }, []);

    useEffect(() => {
        const hasProcessing = docs.some(d => d.status==='processing'||d.status==='pending');
        if (!hasProcessing) return;
        const t = setInterval(() => fetchDocs(currentPage), 5000);
        return () => clearInterval(t);
    }, [docs]);

    const handleDelete = async () => {
        setDeleteLoading(true);
        const token = localStorage.getItem('token');
        try {
            const id = deleteDoc.id||deleteDoc._id;
            const res = await fetch(`${chatbotUrl}/documents/${id}`, { method:'DELETE', headers:{ Authorization:`Bearer ${token}` } });
            if (!res.ok) throw new Error();
            toast.success('Document deleted', { autoClose: 2000 });
            setDeleteDoc(null);
            fetchDocs(currentPage);
        } catch (e) {
            toast.error('Failed to delete document', { autoClose: 3000 });
        } finally { setDeleteLoading(false); }
    };

    const handleDownload = async (doc) => {
        const token = localStorage.getItem('token');
        try {
            const id  = doc.id||doc._id;
            const res = await fetch(`${chatbotUrl}/documents/${id}/download`, { headers:{ Authorization:`Bearer ${token}` } });
            if (!res.ok) throw new Error();
            const blob = await res.blob();
            const url  = window.URL.createObjectURL(blob);
            const a    = document.createElement('a');
            a.href = url; a.download = doc.original_name||'document'; a.click();
            window.URL.revokeObjectURL(url);
        } catch (e) { toast.error('Download failed', { autoClose: 3000 }); }
    };

    const totalPages  = Math.ceil(totalCount / LIMIT);
    const readyCount  = docs.filter(d=>d.status==='ready').length;
    const processingCount = docs.filter(d=>['processing','pending'].includes(d.status)).length;
    const totalChunks = docs.filter(d=>d.status==='ready').reduce((sum,d)=>sum+(d.chunk_count||d.chunks||0),0);

    return (
        <WrapperComponent>
            <div style={s.page}>
                <div style={s.pageHeader}>
                    <div>
                        <h5 style={s.pageTitle}>Knowledge Base</h5>
                        <p style={s.pageSubtitle}>Upload and manage documents that power your AI agents</p>
                    </div>
                </div>

                <UploadZone tags={tags} onUploaded={() => fetchDocs(1)}/>

                {/* Stats */}
                <div style={s.statsRow}>
                    {[
                        { label:'Total Docs',  value: totalCount,                  color:'#1565c0', bg:'#eff6ff', border:'#bfdbfe' },
                        { label:'Ready',       value: readyCount,                  color:'#16a34a', bg:'#f0fdf4', border:'#bbf7d0' },
                        { label:'Processing',  value: processingCount,             color:'#d97706', bg:'#fffbeb', border:'#fde68a' },
                        { label:'Chunks',      value: totalChunks.toLocaleString(), color:'#1565c0', bg:'#eff6ff', border:'#bfdbfe' },
                    ].map(stat => (
                        <div key={stat.label} style={{ padding:'10px 18px', background:stat.bg, border:`1px solid ${stat.border}`, borderRadius:10, display:'flex', gap:8, alignItems:'center' }}>
                            <span style={{ fontSize:18, fontWeight:800, color:stat.color }}>{stat.value}</span>
                            <span style={{ fontSize:12, color:'#6b7280' }}>{stat.label}</span>
                        </div>
                    ))}
                </div>

                {/* Filters */}
                <div style={s.filterRow}>
                    <div style={s.searchWrapper}>
                        <i className="bi bi-search" style={s.searchIcon}></i>
                        <input style={s.searchInput} placeholder="Search filename..." value={search}
                            onChange={e => setSearch(e.target.value)}
                            onKeyDown={e => { if (e.key==='Enter') { setCurrentPage(1); fetchDocs(1); } }}/>
                    </div>
                    <select style={s.select} value={tagFilter} onChange={e => { setTagFilter(e.target.value); setCurrentPage(1); fetchDocs(1); }}>
                        <option value="">All Tags</option>
                        {tags.map(t => <option key={t.id||t._id} value={t.id||t._id}>{t.name||t.tag_name}</option>)}
                    </select>
                    <select style={s.select} value={statusFilter} onChange={e => { setStatusFilter(e.target.value); setCurrentPage(1); fetchDocs(1); }}>
                        <option value="">All Status</option>
                        <option value="ready">Ready</option>
                        <option value="processing">Processing</option>
                        <option value="pending">Pending</option>
                        <option value="failed">Failed</option>
                    </select>
                    <button onClick={() => fetchDocs(currentPage)} style={s.btnSecondary}>
                        <i className="bi bi-arrow-clockwise" style={{ marginRight:6 }}></i>Refresh
                    </button>
                </div>

                {/* Table */}
                <div style={s.tableCard}>
                    {loading ? (
                        <div style={s.centerState}>
                            <div className="spinner-border text-primary" style={{ width:'2rem', height:'2rem' }}></div>
                            <p style={s.stateText}>Loading documents…</p>
                        </div>
                    ) : (
                        <div className="table-responsive">
                            <table style={s.table}>
                                <thead>
                                    <tr style={s.tableHead}>
                                        {['Document','Tag','Size','Chunks','Status','Version','Uploaded',''].map(h => (
                                            <th key={h} style={s.th}>{h}</th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {docs.length===0 ? (
                                        <tr><td colSpan={8} style={{ padding:40, textAlign:'center', color:'#9ca3af' }}>
                                            No documents found. Upload your first document above.
                                        </td></tr>
                                    ) : docs.map(doc => {
                                        const ext      = (doc.source_type||doc.type||doc.original_name?.split('.').pop()||'txt').toLowerCase();
                                        const st       = STATUS_CONFIG[doc.status]||STATUS_CONFIG.pending;
                                        const tc       = TYPE_COLORS[ext]||TYPE_COLORS.txt;
                                        const filename = doc.original_name||'Unknown';
                                        const sizeKb   = doc.file_size_kb||doc.size_kb||(doc.file_size?Math.round(doc.file_size/1024):0);
                                        const chunks   = doc.chunk_count||doc.chunks||0;
                                        const tagName  = doc.tag_name||tags.find(t=>(t.id||t._id)===(doc.tag_id||doc.tag))?.(t=>t.name||t.tag_name)||doc.tag_id||'—';
                                        const isProc   = ['processing','pending'].includes(doc.status);
                                        return (
                                            <tr key={doc.id||doc._id} style={s.tr}
                                                onMouseEnter={e=>e.currentTarget.style.background='#f8fafc'}
                                                onMouseLeave={e=>e.currentTarget.style.background=''}>
                                                <td style={s.td}>
                                                    <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                                                        <span style={{ fontSize:11, fontWeight:700, padding:'2px 6px', borderRadius:4, background:tc.bg, color:tc.color, textTransform:'uppercase', fontFamily:'monospace' }}>{ext}</span>
                                                        <span style={{ fontSize:13, fontWeight:600, color:'#111827', maxWidth:200, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{filename}</span>
                                                    </div>
                                                    {doc.uploaded_by && <p style={{ fontSize:11, color:'#9ca3af', marginTop:2, paddingLeft:44, marginBottom:0 }}>by {doc.uploaded_by}</p>}
                                                </td>
                                                <td style={s.td}><span style={{ fontSize:11, fontWeight:600, padding:'2px 8px', borderRadius:20, background:'#eff6ff', color:'#1565c0', fontFamily:'monospace' }}>{tagName}</span></td>
                                                <td style={s.td}><span style={{ fontSize:13, color:'#6b7280' }}>{sizeKb>0?formatSize(sizeKb):'—'}</span></td>
                                                <td style={s.td}>
                                                    {isProc ? (
                                                        <span style={{ color:'#d97706', fontSize:12, display:'flex', alignItems:'center', gap:4 }}>
                                                            <i className="bi bi-arrow-repeat" style={{ animation:'spin 1s linear infinite' }}></i>Processing
                                                        </span>
                                                    ) : <span style={{ fontSize:13, fontWeight:600, color:'#111827' }}>{chunks.toLocaleString()}</span>}
                                                </td>
                                                <td style={s.td}>
                                                    <span style={{ fontSize:11, fontWeight:600, padding:'3px 8px', borderRadius:20, background:st.bg, color:st.color, border:`1px solid ${st.border}`, display:'inline-flex', alignItems:'center', gap:4 }}>
                                                        <i className={`bi ${st.icon}`} style={{ fontSize:10 }}></i>{st.label}
                                                    </span>
                                                </td>
                                                <td style={{ ...s.td, textAlign:'center' }}><span style={{ fontSize:13, color:'#6b7280' }}>v{doc.version||1}</span></td>
                                                <td style={s.td}><span style={{ fontSize:12, color:'#9ca3af' }}>{doc.created_at||doc.uploaded_at?new Date(doc.created_at||doc.uploaded_at).toLocaleDateString('en-IN',{day:'2-digit',month:'short',year:'numeric'}):'—'}</span></td>
                                                <td style={s.td}>
                                                    <div style={{ display:'flex', gap:6 }}>
                                                        <button onClick={()=>handleDownload(doc)} style={{ padding:'5px 8px', fontSize:12, color:'#1565c0', background:'#eff6ff', border:'none', borderRadius:6, cursor:'pointer' }} title="Download">
                                                            <i className="bi bi-download"></i>
                                                        </button>
                                                        <button onClick={()=>setDeleteDoc(doc)} style={{ padding:'5px 8px', fontSize:12, color:'#dc2626', background:'#fef2f2', border:'none', borderRadius:6, cursor:'pointer' }}>
                                                            <i className="bi bi-trash"></i>
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>

                {totalPages > 1 && (
                    <PaginationComponent currentPage={currentPage} totalPages={totalPages} onPageChange={(p)=>{ setCurrentPage(p); fetchDocs(p); }}/>
                )}

                <ToastContainer/>
                <ConfirmModal
                    open={!!deleteDoc} onClose={()=>setDeleteDoc(null)}
                    onConfirm={handleDelete} loading={deleteLoading}
                    message={`Delete "${deleteDoc?.original_name}"? It will be removed from MongoDB, Qdrant vectors, and the file server. This cannot be undone.`}
                />
            </div>
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </WrapperComponent>
    );
};

export default KnowledgeBase;

const s = {
    page:         { display:'flex', flexDirection:'column', gap:16, fontFamily:"'Segoe UI', system-ui, sans-serif" },
    pageHeader:   { display:'flex', alignItems:'flex-start', justifyContent:'space-between' },
    pageTitle:    { margin:0, fontSize:20, fontWeight:700, color:'#111827' },
    pageSubtitle: { margin:'2px 0 0', fontSize:13, color:'#6b7280' },
    uploadCard:   { background:'white', borderRadius:12, border:'1px solid #e5e7eb', padding:20, boxShadow:'0 1px 6px rgba(0,0,0,0.06)' },
    uploadTitle:  { margin:'0 0 16px', fontSize:14, fontWeight:700, color:'#111827' },
    statsRow:     { display:'flex', gap:12, flexWrap:'wrap' },
    filterRow:    { display:'flex', gap:10, flexWrap:'wrap', alignItems:'center' },
    searchWrapper:{ position:'relative', flex:1, minWidth:200 },
    searchIcon:   { position:'absolute', left:12, top:'50%', transform:'translateY(-50%)', color:'#9ca3af', fontSize:13 },
    searchInput:  { width:'100%', padding:'8px 12px 8px 34px', fontSize:13, border:'1.5px solid #e5e7eb', borderRadius:8, outline:'none', boxSizing:'border-box', color:'#374151' },
    select:       { padding:'8px 12px', fontSize:13, border:'1.5px solid #e5e7eb', borderRadius:8, outline:'none', color:'#374151', background:'white' },
    fieldLabel:   { fontSize:12, fontWeight:600, color:'#6b7280', display:'block', marginBottom:6 },
    tableCard:    { background:'white', borderRadius:12, border:'1px solid #e5e7eb', overflow:'hidden', boxShadow:'0 1px 6px rgba(0,0,0,0.06)' },
    centerState:  { display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', gap:12, padding:'60px 20px' },
    stateText:    { fontSize:14, color:'#6b7280', margin:0 },
    table:        { width:'100%', borderCollapse:'collapse', fontSize:14 },
    tableHead:    { background:'#eff6ff' },
    th:           { padding:'12px 14px', textAlign:'left', fontSize:11, fontWeight:700, color:'#1565c0', textTransform:'uppercase', letterSpacing:'0.04em', borderBottom:'2px solid #bfdbfe', whiteSpace:'nowrap' },
    tr:           { borderBottom:'1px solid #f3f4f6', transition:'background 0.1s' },
    td:           { padding:'12px 14px', color:'#374151', verticalAlign:'middle' },
    btnSecondary: { display:'inline-flex', alignItems:'center', padding:'8px 12px', background:'#f3f4f6', color:'#374151', border:'1px solid #e5e7eb', borderRadius:8, fontSize:13, fontWeight:600, cursor:'pointer', whiteSpace:'nowrap' },
};
